import React, { useState } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';
import { RestituteParticipantBL } from '../../../utilities/BL/PersonalRoulette';

/**
 * Modal de Soporte para Restituir Oportunidad y Reactivar Aficionado (HU-06)
 * Permite archivar el intento anterior conservando trazabilidad de auditoría
 * y liberando al aficionado para volver a registrarse y girar con su mismo correo o teléfono.
 */
export const PersonalRouletteResetModal = ({
    isOpen,
    onClose,
    participant = null,
    roomId = null,
    onResetSuccess = null
}) => {
    const { showMessage } = useFloatMessage();

    const REASON_PRESETS = [
        'Falla de conectividad / Intermitencia de red',
        'Recarga accidental / Error de navegador',
        'Bloqueo o congelamiento de pantalla',
        'Otro'
    ];

    const [selectedPreset, setSelectedPreset] = useState(REASON_PRESETS[0]);
    const [customReason, setCustomReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen || !participant) return null;

    const isWinner = participant.isWinner ?? (participant.prize?.isWinner ?? false);
    const hasRedeemed = participant.hasRedeemedPrize || participant.isRedeemed || participant.status === 'REDEEMED';
    const prizeTitle = participant.prizeTitleSnapshot || participant.prizeText || participant.prize?.textEs || (isWinner ? 'Premio Oficial' : 'Pase Cortesía');
    const passCode = participant.passCode || participant.participationTicket || participant.folio || '-';

    const handleConfirm = async (e) => {
        if (e) e.preventDefault();

        const reasonFinal = selectedPreset === 'Otro'
            ? customReason.trim()
            : (customReason.trim() ? `${selectedPreset} - ${customReason.trim()}` : selectedPreset);

        if (!reasonFinal || reasonFinal.length < 3) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Por favor ingresa un motivo válido para la restitución (mínimo 3 caracteres).'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                participantId: participant.id,
                reason: reasonFinal,
                roomId: roomId || participant.roomId || null
            };

            const res = await RestituteParticipantBL(payload);
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `¡Oportunidad restituida con éxito para ${participant.fullName || passCode}!`
                });
                if (onResetSuccess) onResetSuccess(participant.id);
                if (onClose) onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'No fue posible restituir la participación.'
                });
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al intentar restituir la participación.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    zIndex: 99998,
                    backdropFilter: 'blur(4px)'
                }}
            />

            {/* Modal Box */}
            <div
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '95%',
                    maxWidth: '580px',
                    zIndex: 99999
                }}
            >
                <div className="card shadow-lg border-0 rounded-4 overflow-hidden text-dark bg-white">
                    {/* Header */}
                    <div className="bg-warning text-dark p-3 px-4 d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-arrow-counterclockwise fs-4 fw-bold"></i>
                            <h5 className="mb-0 fw-bold">Dar otra oportunidad al aficionado</h5>
                        </div>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            disabled={isSubmitting}
                            aria-label="Cerrar"
                        ></button>
                    </div>

                    {/* Body */}
                    <div className="card-body p-4">
                        {/* Tarjeta de información del aficionado */}
                        <div className="bg-light p-3 rounded-3 border mb-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                    <h6 className="fw-bold mb-1 text-dark">{participant.fullName || 'Aficionado'}</h6>
                                    <div className="text-secondary small">
                                        <i className="bi bi-envelope me-1"></i>{participant.email || 'Sin correo'}
                                        <span className="mx-2">•</span>
                                        <i className="bi bi-telephone me-1"></i>{participant.phone || 'Sin teléfono'}
                                    </div>
                                </div>
                                <span className="badge bg-dark fs-6 px-2 py-1 font-monospace">
                                    {passCode}
                                </span>
                            </div>

                            <div className="pt-2 border-top d-flex flex-wrap align-items-center justify-content-between gap-2 text-muted small">
                                <div className="d-flex align-items-center gap-1.5">
                                    <span className="fw-semibold text-secondary">Resultado:</span>
                                    <span className={`badge ${isWinner ? 'bg-danger text-white' : 'bg-secondary text-white'}`}>
                                        <i className={`bi ${isWinner ? 'bi-trophy-fill me-1' : 'bi-ticket-fill me-1'}`}></i>
                                        {prizeTitle}
                                    </span>
                                </div>

                                <div className="d-flex align-items-center gap-2">
                                    <span className="fw-semibold text-secondary">Estatus:</span>
                                    {participant.isArchived ? (
                                        <span className="badge bg-secondary">Archivado</span>
                                    ) : hasRedeemed ? (
                                        <span className="badge bg-success">🎁 Canjeado</span>
                                    ) : isWinner ? (
                                        <span className="badge bg-warning text-dark">★ Ganador</span>
                                    ) : (
                                        <span className="badge bg-light text-dark border">Cortesía</span>
                                    )}
                                    <span className="badge bg-light text-muted border">
                                        Intento #{participant.attemptNumber || 1}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Alerta informativa en amarillo */}
                        <div className="alert alert-warning d-flex align-items-start gap-2 py-2.5 px-3 small rounded-3 mb-3">
                            <i className="bi bi-exclamation-triangle-fill fs-5 text-warning-emphasis flex-shrink-0 mt-0"></i>
                            <div>
                                <strong>Acción de Auditoría:</strong> Al otorgar otra oportunidad, se archivará el intento anterior conservando la trazabilidad de auditoría. El aficionado podrá volver a registrarse y girar nuevamente con su mismo correo o número de teléfono.
                            </div>
                        </div>

                        {/* Selector de motivos rápidos */}
                        <div className="mb-3">
                            <label className="form-label fw-semibold small text-secondary mb-2">
                                Selecciona el motivo:
                            </label>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                {REASON_PRESETS.map((preset) => {
                                    let icon = 'bi-info-circle';
                                    if (preset.includes('conectividad')) icon = 'bi-wifi';
                                    else if (preset.includes('Recarga')) icon = 'bi-arrow-repeat';
                                    else if (preset.includes('Bloqueo')) icon = 'bi-phone';
                                    else if (preset === 'Otro') icon = 'bi-pencil-square';

                                    const isSelected = selectedPreset === preset;
                                    return (
                                        <button
                                            key={preset}
                                            type="button"
                                            onClick={() => setSelectedPreset(preset)}
                                            className={`btn btn-sm rounded-pill fw-semibold px-3 py-1.5 d-flex align-items-center gap-1.5 transition-all ${
                                                isSelected
                                                    ? 'btn-warning text-dark border-warning shadow-sm'
                                                    : 'btn-outline-secondary'
                                            }`}
                                        >
                                            {isSelected ? (
                                                <i className="bi bi-check-circle-fill text-dark"></i>
                                            ) : (
                                                <i className={`bi ${icon}`}></i>
                                            )}
                                            <span>{preset}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            <label className="form-label fw-semibold small text-secondary mb-1">
                                {selectedPreset === 'Otro' ? 'Escribe el motivo detallado (obligatorio):' : 'Detalle o notas adicionales (opcional):'}
                            </label>
                            <textarea
                                className="form-control small"
                                rows="2"
                                placeholder={selectedPreset === 'Otro' ? 'Especifica la razón de soporte técnico...' : 'Ej. Se interrumpió la conexión durante la animación del giro en el stand...'}
                                value={customReason}
                                onChange={(e) => setCustomReason(e.target.value)}
                                required={selectedPreset === 'Otro'}
                            ></textarea>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="card-footer bg-light p-3 px-4 d-flex justify-content-end gap-2 border-top">
                        <button
                            type="button"
                            className="btn btn-outline-secondary px-4 fw-semibold"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-warning text-dark px-4 fw-bold d-flex align-items-center gap-2 shadow-sm"
                            onClick={handleConfirm}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                    <span>Otorgando oportunidad...</span>
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-arrow-counterclockwise fs-5"></i>
                                    <span>★ Confirmar y Dar Otra Oportunidad</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PersonalRouletteResetModal;
